/* eslint-disable max-lines-per-function */
import { CommentOutlined as CommentOutlined, Autorenew as LoadingOutlined, FileUpload as UploadOutlined, SaveOutlined as SaveOutlined, Sync as SyncOutlined, ErrorOutline as ExclamationCircleOutlined } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { AutoComplete } from "../AutoComplete/AutoComplete";
import { enterShouldSend } from "./enterToSend";
import { Button } from "../Button/Button";
import { Card } from "../Card/Card";
import { Col, Row } from "../Layout/Grid";
import { Input } from "../Input/Input";
import { Tag } from "../Tag/Tag";
import { Tooltip } from "../Tooltip/Tooltip";
import {
  Upload,
  type UploadProps,
  type RcFile,
  type UploadFileStatus,
  type UploadProgressEvent,
} from "../Upload/Upload";
import { CheckCircleOutline as CheckCircleOutlined, CancelOutlined as CloseCircleOutlined } from "@mui/icons-material";

import {
  CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { SAVE_STORED_PROMPT, GET_STORED_PROMPTS_LIST, CHAT_SKILLS_FOR_INPUT, CHAT_AGENTS_FOR_INPUT } from "./graphql";
import { ChatMessageHead } from "./ChatMessageHead";
import { CHAT_MAX_WIDTH } from "./chatLayout";
import { ChatMessageItem } from "./ChatMessageItem";
import { ChatMessage } from "./types";
import { useMemo } from "react";
import DropZone from "./dropzone";
import uuid from "../../utils/uuid";

export const PresignedTempUrl = gql(/* GraphQL */ `
  query PresignedTempUrl($input: PresignedTempUrlInput!) {
    presignedTempUrl(input: $input) {
      bucket
      key
      url
    }
  }
`);

import axios from "axios";
import { ChatComponentFiles, cleanupChatStore, useChatStore } from "./stores";
import { gql } from "_graphql-types-frontend";
import {
  mergeUploadedFileList,
  commandQuery,
  replaceCommand,
  replaceSlashCommand,
  slashCommandQuery,
  useContainerWidth,
  startsNewSpeakerRun,
} from "./utils";

const MarkdownCard = styled(Card)({
  "& .markdown-li": {
    display: "list-item",
  },
  "& .markdown-ol": {
    listStyle: "decimal outside none",
    display: "block",
    margin: "1em 0",
    padding: "0 0 0 2em",
  },
  "& .markdown-ul": {
    listStyle: "disc outside none",
    display: "block",
    margin: "1em 0",
    padding: "0 0 0 2em",
  },
  "& .markdown-table": {
    width: "initial",
    "& th": {
      fontWeight: "bold",
    },
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "#f2f2f2",
    },
  },
  "& code": {
    backgroundColor: "#f2f2f2",
    borderRadius: "3px",
    padding: "0 3px",
  },
});

const MARKDOWN_CARD_STYLE = {
  borderRadius: "10px",
  width: "100%",
};

// A clean, intentional white margin below the composer (MUI CardContent's default
// `:last-child` is 24px which read as an odd gap; 0 left it looking flush/missing).
const CARD_BODY_STYLE = { paddingBottom: 16 };

const TEXT_AREA_STYLE = {
  width: `100%`,
  borderRadius: "5px",
};

// Let the composer size to its content so the multiline textarea can grow (shift+enter).
// A fixed `height: inherit` here pinned it and interfered with autosize.
const AUTOCOMPLETE_STYLE = { width: "100%" };

export type ChatBoxFileUpload = {
  enabled: boolean;
  onInsert?: ({ file }: { file: RcFile }) => void;
  onSuccess?: ({
    file,
    bucket,
    key,
    url,
  }: {
    file: RcFile;
    bucket: string;
    key: string;
    url: string;
  }) => void;
  onError?: ({ file, error }: { file: RcFile; error: any }) => void;
};

export const ChatComponent = ({
  chatMessages,
  handleUserMessage,
  userText,
  setUserText,
  fileUpload,
  agentWorking,
  responseButtonClickAction,
  cancelAgentFocus,
  cardStyles,
  onNewChat,
  agentCharacter,
  controlButtons,
  chatStoreId,
  knowledgeBase,
  isFullscreen,
  onRecallQueued,
  aboveInput,
}: {
  chatMessages: (ChatMessage & { key?: string })[];
  handleUserMessage: (
    text?: string,
    files?: ChatComponentFiles[]
  ) => Promise<void>;
  userText: string;
  setUserText: (text: string) => void;
  fileUpload?: ChatBoxFileUpload;
  responseButtonClickAction?: (value: string | undefined) => void;
  agentWorking: boolean;
  cancelAgentFocus?: () => void;
  cardStyles?: CSSProperties;
  onNewChat?: () => void;
  agentCharacter: {
    name: string;
  };
  controlButtons?: React.ReactNode;
  chatStoreId?: string;
  knowledgeBase?: {
    id: number;
    name: string;
    status: string;
    noteCount?: number;
    documentCount?: number;
    investmentCount?: number;
    firmCount?: number;
    companyCount?: number;
    onRemove: () => void;
    onRefresh?: () => void;
  };
  isFullscreen?: boolean;
  onRecallQueued?: () => void;
  aboveInput?: React.ReactNode;
}) => {
  const storeId = useMemo(() => {
    return chatStoreId ?? `chat-store-${Math.random()}`;
  }, [chatStoreId]);

  const store = useChatStore(storeId);

  // Access state
  const localChatId = store(state => state.localChatId);
  const fileList = store(state => state.fileList);
  const autoScroll = store(state => state.autoScroll);
  const storedPromptSearch = store(state => state.storedPromptSearch);
  const saveButtonState = store(state => state.saveButtonState);

  // Use actions
  const setLocalChatId = store(state => state.setLocalChatId);
  const setFileList = store(state => state.setFileList);
  const setAutoScroll = store(state => state.setAutoScroll);
  const setStoredPromptSearch = store(state => state.setStoredPromptSearch);
  const setSavedButtonSet = store(state => state.setSaveButtonState);

  useEffect(() => {
    // If chat store is specified it will outlive the component
    // allows for instance ranger chat to be used in multiple places
    // and not lose state
    return () => {
      if (!chatStoreId) {
        cleanupChatStore(storeId);
      }
    };
  }, [storeId]);

  const apolloClient = useApolloClient();
  const [saveStoredPrompt, { loading: savePromptLoading, error }] =
    useMutation(SAVE_STORED_PROMPT);

  const { data: storedPromptsData } = useQuery(GET_STORED_PROMPTS_LIST, {
    skip: !storedPromptSearch,
    variables: { filter: { q: storedPromptSearch } },
  });

  const { data: skillsData } = useQuery(CHAT_SKILLS_FOR_INPUT, {
    fetchPolicy: "cache-and-network",
  });
  const skills: Array<{ id: number; name: string; description: string }> =
    (skillsData as any)?.myActiveChatSkills ?? [];

  // Where the caret sat at the last edit — a slash command is whatever is being
  // typed there, so the menu opens mid-message and not only on a lone "/".
  const [caretPosition, setCaretPosition] = useState(0);
  // The composer's textarea is built by the AutoComplete compat wrapper from this
  // child's props, so it can't take a ref; capture the element as the user types.
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const { data: agentsData } = useQuery(CHAT_AGENTS_FOR_INPUT, {
    fetchPolicy: "cache-and-network",
  });
  const agents: Array<{ id: number; name: string; description: string }> =
    (agentsData as any)?.mySpawnableChatAgents ?? [];

  /**
   * `@name` addresses a turn to a configured agent, mirroring `/name` for
   * skills — including mid-message, through the same caret-anchored matcher
   * (#5246). The anchor is what makes "@" safe to read anywhere: it only
   * counts at the start or after whitespace, so the "@" in an email address
   * never opens the menu. The server applies the same rule, and still only
   * treats a mention as one when it RESOLVES to an agent.
   */
  const agentSuggestions = useMemo(() => {
    const query = commandQuery(userText, caretPosition, "@");
    if (query === null) return [];
    return agents.filter(
      a => query === "" || a.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [userText, caretPosition, agents]);

  const skillSuggestions = useMemo(() => {
    const query = slashCommandQuery(userText, caretPosition);
    if (query === null) return [];
    return skills.filter(
      s => query === "" || s.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [userText, caretPosition, skills]);

  const autocompleteOptions = useMemo(() => {
    const skillOpts = skillSuggestions.map(s => ({
      value: `/${s.name} `,
      skillName: s.name,
      label: (
        <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          <span style={{ fontWeight: 500 }}>/{s.name}</span>
          <span style={{ fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis" }}>
            {s.description}
          </span>
        </div>
      ),
    }));
    const agentOpts = agentSuggestions.map(a => ({
      value: `@${a.name} `,
      agentName: a.name,
      label: (
        <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          <span style={{ fontWeight: 500 }}>@{a.name}</span>
          <span style={{ fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis" }}>
            {a.description}
          </span>
        </div>
      ),
    }));
    const promptOpts =
      storedPromptsData?.storedPromptsList.items.map(o => ({ value: o.prompt })) ?? [];

    // "@" and "/" are mutually exclusive prefixes, so at most one of these is
    // ever non-empty; grouping only matters against saved prompts.
    const groups = [
      agentOpts.length > 0 ? { label: "Agents", options: agentOpts } : null,
      skillOpts.length > 0 ? { label: "Skills", options: skillOpts } : null,
      promptOpts.length > 0 ? { label: "Saved prompts", options: promptOpts } : null,
    ].filter(Boolean) as Array<{ label: string; options: unknown[] }>;

    if (groups.length > 1) return groups;
    if (agentOpts.length > 0) return agentOpts;
    if (skillOpts.length > 0) return skillOpts;
    return promptOpts;
  }, [agentSuggestions, skillSuggestions, storedPromptsData]);

  const localOnNewChat = useCallback(() => {
    setLocalChatId(localChatId + 1);
    setUserText("");
    onNewChat?.();
  }, [onNewChat, setUserText, setFileList, setLocalChatId, localChatId]);

  const ref = useRef<HTMLDivElement>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);

  const shouldAutoScroll = useCallback(() => {
    if (!chatContainerRef.current) return false;
    const currentScrollTop = chatContainerRef.current.scrollTop;
    const scrollHeight = chatContainerRef.current.scrollHeight;
    const clientHeight = chatContainerRef.current.clientHeight;
    const threshold = 50;

    return currentScrollTop + clientHeight >= scrollHeight - threshold;
  }, [chatContainerRef]);

  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;
    setAutoScroll(shouldAutoScroll());
  }, [setAutoScroll, shouldAutoScroll]);

  function scrollIntoView() {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    const container = chatContainerRef.current;

    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  useEffect(() => {
    if (autoScroll && chatContainerRef.current) {
      scrollIntoView();
    }
  }, [chatMessages, autoScroll]);

  const handleSendMessage = useCallback(() => {
    setAutoScroll(true);
    // only send files that are done uploading
    handleUserMessage(
      userText,
      fileList.filter(f => f.status === "done")
    );
    scrollIntoView();
  }, [setAutoScroll, handleUserMessage, scrollIntoView, userText, fileList]);

  const chatMessagesStyle: CSSProperties = useMemo(() => {
    return {
      overflowY: "auto",
      // width:100% + margin:10px made this wider than its container, and overflowY:auto
      // promotes overflow-x to auto per spec -> a phantom horizontal scrollbar. The flex
      // child already fills the width; only vertical scrolling is wanted.
      overflowX: "hidden",
      // Spacing INSIDE the scroller, not a margin around it: a margin inset
      // the scrollbar from the panel's edge so it floated over the messages.
      // Equal left/right, so the user's side mirrors the responders'.
      padding: "10px 16px",
      flexGrow: 1,
      minHeight: "200px",
    };
  }, []);

  const handleFiles = (
    files: FileList,
    fileList: ChatComponentFiles[],
    setFileList: React.Dispatch<React.SetStateAction<ChatComponentFiles[]>>
  ) => {
    if (!files.length) return;

    const newFileList: ChatComponentFiles[] = Array.from(files).map(file => {
      const rcFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      }) as RcFile;

      // Now manually add required RcFile fields
      rcFile.uid = uuid(); // or nanoid()

      return {
        uid: rcFile.uid,
        url: "",
        bucket: "",
        key: "",
        name: file.name,
        status: "uploading" as UploadFileStatus,
        originFileObj: rcFile as RcFile, // antd's types seem off.
      };
    });

    const updatedFileList = new DataTransfer();
    if (fileList) {
      fileList.forEach(file => {
        if (file.originFileObj instanceof File) {
          updatedFileList.items.add(file.originFileObj);
        }
      });
    }
    newFileList.forEach(file => {
      if (file.originFileObj instanceof File) {
        updatedFileList.items.add(file.originFileObj);
      }
    });

    setFileList(prev => [...prev, ...newFileList]);
  };

  // Drag and Drop
  const onDrop: (files: FileList) => void = useCallback(
    files => {
      handleFiles(files, fileList, setFileList);
    },
    [fileList, setFileList, fileUpload]
  );

  // Paste
  const onPasteCapture = useCallback(
    (e: React.ClipboardEvent) => {
      const files = e.clipboardData.files;
      handleFiles(files, fileList, setFileList);
    },
    [fileList, setFileList, fileUpload]
  );

  const uploadedUIDs = useRef<Set<string>>(new Set());

  const onNewFile: NonNullable<UploadProps["customRequest"]> = async ({
    file,
    onProgress,
  }) => {
    if (!fileUpload?.enabled) return;
    if (!(file instanceof File)) {
      return;
    }
    // Single upload entry point for both attach paths (button -> customRequest,
    // drag-and-drop/paste -> the effect below), so claim the uid here rather than
    // at each call site — otherwise a file reachable from both uploads twice.
    const uid = (file as RcFile).uid;
    if (uid) {
      if (uploadedUIDs.current.has(uid)) return;
      uploadedUIDs.current.add(uid);
    }
    const filename = file.name;

    fileUpload?.onInsert &&
      (await fileUpload.onInsert({ file: file as RcFile }));

    try {
      const {
        data: { presignedTempUrl },
      } = await apolloClient.query({
        query: PresignedTempUrl,
        variables: {
          input: {
            filename,
          },
        },
        fetchPolicy: "no-cache",
      });
      if (!presignedTempUrl) {
        throw Error("No presignedTempUrl (entitlements?)");
      }
      const { bucket, key, url } = presignedTempUrl;
      if (!bucket || !key || !url) {
        throw Error("No bucket, key, or url (entitlements?)");
      }

      await axios.put(url, file, {
        // Without a timeout, a stalled S3 PUT never settles — the chip spins
        // forever with no error state and the uploader never retries (each
        // file is single-attempt). 5 minutes is generous for large PDFs over
        // VPN; on expiry the catch below flips the chip to its error state so
        // the user can remove and re-add the file.
        timeout: 5 * 60_000,
        onUploadProgress: event => {
          const { loaded, total } = event;
          onProgress?.({
            percent: total ? Math.round((loaded / total) * 100) : 0,
          } as UploadProgressEvent);
        },
      });

      fileUpload?.onSuccess &&
        (await fileUpload.onSuccess({
          file: file as RcFile,
          bucket,
          key,
          url,
        }));

      setFileList(prev =>
        prev.map(f =>
          f.uid === (file as RcFile).uid
            ? { ...f, status: "done" as const, bucket, key, url }
            : f
        )
      );
    } catch (error) {
      fileUpload?.onError &&
        (await fileUpload.onError({ file: file as RcFile, error }));
      setFileList(prev =>
        prev.map(f =>
          f.uid === (file as RcFile).uid ? { ...f, status: "error" } : f
        )
      );
    }
  };

  const containerWidth = useContainerWidth(chatContainerRef);
  const showAvatarColumn = containerWidth > 280;

  const [containerHeight, setContainerHeight] = useState("300px");

  useEffect(() => {
    if (cardStyles && cardStyles.height) return;

    // requestAnimationFrame polling, restoring the pre-#4666 behaviour. The
    // intervening ResizeObserver implementation only fired when the chat's
    // own contentRect changed — not when an ancestor resized — so in
    // mercury, navigating between the investment list and detail pages
    // (which changes navbar height via the heading-yield portal, which
    // shrinks the sidebar via its `height: calc(100% - navBarHeight)`)
    // left the chat with a stale containerHeight that overflowed the
    // sidebar and pushed the input row off-screen.
    //
    // rAF is bounded to ~60Hz, browsers throttle it on hidden tabs, and
    // each tick is one getBoundingClientRect + string compare. The
    // setState is idempotent so a stable layout doesn't cause re-renders.
    let frameId: number;
    const update = () => {
      if (ref.current) {
        const heightToBottomOfPage =
          window.innerHeight - ref.current.getBoundingClientRect().top;
        // Offset for the card chrome so the composer sits just above the viewport
        // bottom with a small margin. Total space below the composer is
        // (offset - top padding) ~= 20px: a clean margin, between the old 34px "too
        // big" gap and the 10px "looks flush" that 26 produced.
        const next = `${heightToBottomOfPage - 36}px`;
        setContainerHeight(prev => (prev === next ? prev : next));
      }
      frameId = requestAnimationFrame(update);
    };

    update();
    return () => cancelAnimationFrame(frameId);
  }, [ref.current]);

  useEffect(() => {
    if (!fileUpload) return;

    fileList.forEach(file => {
      const { uid, status, percent, originFileObj } = file;
      if (!uid) return;

      // Only upload once per file with "uploading" status and not yet tracked
      if (
        status === "uploading" &&
        !percent &&
        !uploadedUIDs.current.has(uid)
      ) {
        onNewFile({
          file: originFileObj as RcFile,
          filename: file.name,
          onProgress: ({ percent }) => {
            setFileList(prev =>
              prev.map(f =>
                f.uid === uid ? { ...f, percent, status: "uploading" } : f
              )
            );
          },
          onSuccess: () => {
            setFileList(prev =>
              prev.map(f => (f.uid === uid ? { ...f, status: "done" } : f))
            );
          },
          onError: err => {
            setFileList(prev =>
              prev.map(f => (f.uid === uid ? { ...f, status: "error" } : f))
            );
          },
          action: "dummy", // required
          method: "POST", // required
        });
      }
    });
  }, [fileList, fileUpload]);

  return (
    <Row>
      <Col span={24}>
        <DropZone onDrop={onDrop} enabled={!!fileUpload?.enabled}>
          <MarkdownCard ref={ref} style={MARKDOWN_CARD_STYLE} bodyStyle={CARD_BODY_STYLE}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: containerHeight, // Adjust as needed
                ...cardStyles,
                ...(isFullscreen ? {} : { maxHeight: "800px" }),
              }}
            >
              <div
                ref={chatContainerRef}
                data-cy="chat-messages"
                style={chatMessagesStyle}
              >
                <Row style={{ maxWidth: CHAT_MAX_WIDTH, margin: "0 auto" }}>
                  {chatMessages.map((message, imessage) => (
                    <Fragment key={message.key ?? imessage}>
                      {/* A new head whenever the speaker changes — the
                          user, the assistant, or a particular agent. */}
                      {startsNewSpeakerRun(chatMessages[imessage - 1], message) && (
                        <ChatMessageHead
                          type={message.type}
                          separator={!!imessage}
                          agentCharacter={agentCharacter}
                          speaker={message.speaker}
                        />
                      )}
                      <ChatMessageItem
                        message={message}
                        onResponseButtonClick={responseButtonClickAction}
                        showAvatarColumn={showAvatarColumn}
                        isStreaming={
                          agentWorking &&
                          imessage === chatMessages.length - 1 &&
                          message.type === "agent"
                        }
                      />
                    </Fragment>
                  ))}
                </Row>
              </div>
              {aboveInput}
              <div
                style={{
                  flexShrink: 0,
                  width: "100%",
                  maxWidth: CHAT_MAX_WIDTH,
                  margin: "0 auto",
                }}
              >
                <Row>
                  <AutoComplete
                    value={userText}
                    onSelect={(value, option) => {
                      const { skillName, agentName } = (option ?? {}) as {
                        skillName?: string;
                        agentName?: string;
                      };
                      // A saved prompt IS the message, so it still replaces the
                      // whole input; a skill or an agent only replaces its own
                      // command token, leaving the surrounding text alone.
                      const name = skillName ?? agentName;
                      if (!name) {
                        setUserText(value);
                        return;
                      }
                      const next = replaceCommand(
                        userText,
                        caretPosition,
                        skillName ? "/" : "@",
                        name
                      );
                      setUserText(next.text);
                      setCaretPosition(next.caret);
                      // React rewrites the textarea's value on the next commit,
                      // which parks the caret at the end — put it back after that.
                      requestAnimationFrame(() => {
                        inputRef.current?.focus();
                        inputRef.current?.setSelectionRange(next.caret, next.caret);
                      });
                    }}
                    // no clear "x": it added a floating endAdornment that inset the
                    // textarea scrollbar and interfered with multiline autosize.
                    allowClear={false}
                    style={AUTOCOMPLETE_STYLE}
                    options={autocompleteOptions as any}
                  >
                    <Input.TextArea
                      value={userText}
                      style={TEXT_AREA_STYLE}
                      placeholder="Message..."
                      id="sidebar-chat-input"
                      aria-label="Chat message input"
                      data-cy="chat-input"
                      autoSize={{ minRows: 1, maxRows: 10 }}
                      onPasteCapture={onPasteCapture}
                      onChange={event => {
                        inputRef.current = event.target;
                        setUserText(event.target.value);
                        setCaretPosition(
                          event.target.selectionStart ?? event.target.value.length
                        );
                        if (event.target.value[0] === "/") {
                          setStoredPromptSearch(event.target.value.slice(1));
                        } else {
                          setStoredPromptSearch(null);
                        }
                      }}
                      onKeyDown={event => {
                        if (event.key === "ArrowUp" && !userText && onRecallQueued) {
                          event.preventDefault();
                          onRecallQueued();
                        }
                        // Enter belongs to the suggestion list when one of its
                        // options is highlighted — see enterShouldSend.
                        if (enterShouldSend(event)) {
                          event.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                  </AutoComplete>
                </Row>
                <Row>
                  <div
                    className="d-flex justify-between mt-10 full-width"
                    // flexWrap so a narrow sidebar drops the Stop/Send group onto
                    // its own row BELOW the icons, instead of overflowing the panel.
                    // The icon group itself stays a single row (nowrap) so it never
                    // collapses into a clunky 2x2 grid.
                    style={{ alignItems: "center", flexWrap: "wrap", gap: "8px" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                      {onNewChat && (
                        <Tooltip title="Start A New Chat">
                          <Button
                            key={"newChat"}
                            onClick={localOnNewChat}
                            type="default"
                            shape="circle"
                            icon={<CommentOutlined />}
                            data-cy="new-chat-button"
                          ></Button>
                        </Tooltip>
                      )}

                      <Tooltip
                        title={
                          saveButtonState === "saved"
                            ? "Prompt saved!"
                            : "Save Message For Later"
                        }
                        open={saveButtonState === "saved" ? true : undefined}
                      >
                        <Button
                          icon={
                            saveButtonState === null ||
                            saveButtonState === "saved" ? (
                              <SaveOutlined />
                            ) : (
                              <LoadingOutlined />
                            )
                          }
                          style={
                            saveButtonState === "saved"
                              ? {
                                  backgroundColor: "lightGreen",
                                }
                              : {}
                          }
                          disabled={!userText?.trim()}
                          onClick={() => {
                            if (!userText?.trim()) return;
                            saveStoredPrompt({
                              variables: { input: { prompt: userText } },
                              refetchQueries: ["GetStoredPromptsList"],
                            }).then(() => {
                              setSavedButtonSet("saved");
                              setTimeout(() => {
                                setSavedButtonSet(null);
                              }, 5000);
                            });
                          }}
                          shape="circle"
                        />
                      </Tooltip>
                      {!!fileUpload?.enabled && (
                        <Upload
                          key={localChatId}
                          data-cy="chat-file-upload"
                          name="upload"
                          fileList={fileList}
                          multiple
                          showUploadList={false}
                          // Without customRequest nothing was ever uploaded for
                          // files picked with this button: they landed in the
                          // store as "done" with no bucket/key, so the chip went
                          // green and the send then dropped them (chatContext.files
                          // arrived empty). Same uploader as drag-and-drop/paste.
                          customRequest={onNewFile}
                          onChange={info => {
                            setFileList(prev =>
                              mergeUploadedFileList(
                                prev,
                                info.fileList as ChatComponentFiles[]
                              )
                            );
                          }}
                        >
                          <Tooltip title="Add attachment">
                            <Button
                              key={"newChat"}
                              type="default"
                              shape="circle"
                              icon={<UploadOutlined />}
                              data-cy="new-chat-button"
                            ></Button>
                          </Tooltip>
                        </Upload>
                      )}
                      {controlButtons}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {agentWorking && cancelAgentFocus && (
                        <Button
                          type="default"
                          // size="small" so these sit at the ~32px icon-button
                          // scale; the default "middle" carries a minHeight:40
                          // (meant for aligning with 40px inputs) that made Send
                          // look oversized next to the composer's icon buttons.
                          size="small"
                          data-cy="chat-stop-button"
                          style={{
                            borderRadius: "5px",
                          }}
                          onClick={cancelAgentFocus}
                        >
                          Stop
                        </Button>
                      )}
                      <Button
                        type="primary"
                        size="small"
                        data-cy="chat-send-button"
                        style={{
                          width: "100px",
                          borderRadius: "5px",
                        }}
                        onClick={() => handleSendMessage()}
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </Row>
                <Row>
                  {((fileUpload?.enabled && fileList.length > 0) ||
                    knowledgeBase) && (
                    <Col span={24} style={{ marginTop: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {knowledgeBase && (
                          <Tag
                            data-cy="chat-knowledge-base-tag"
                            closable
                            onClose={knowledgeBase.onRemove}
                            icon={
                              knowledgeBase.status === "ready" ? (
                                <CheckCircleOutlined />
                              ) : knowledgeBase.status === "pending" ? (
                                <SyncOutlined sx={{ animation: "rcspin 1s linear infinite", "@keyframes rcspin": { to: { transform: "rotate(360deg)" } } }} />
                              ) : knowledgeBase.status === "failed" ? (
                                <ExclamationCircleOutlined />
                              ) : (
                                <SyncOutlined sx={{ animation: "rcspin 1s linear infinite", "@keyframes rcspin": { to: { transform: "rotate(360deg)" } } }} />
                              )
                            }
                            color={
                              knowledgeBase.status === "ready"
                                ? "success"
                                : knowledgeBase.status === "pending"
                                  ? "processing"
                                  : knowledgeBase.status === "failed"
                                    ? "error"
                                    : "default"
                            }
                          >
                            <span
                              style={{
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {knowledgeBase.name}
                              {knowledgeBase.status === "ready" && (
                                <span
                                  style={{ marginLeft: 4, fontSize: "0.85em" }}
                                >
                                  (
                                  {[
                                    knowledgeBase.noteCount && `${knowledgeBase.noteCount}n`,
                                    knowledgeBase.documentCount && `${knowledgeBase.documentCount}d`,
                                    knowledgeBase.investmentCount && `${knowledgeBase.investmentCount}i`,
                                    knowledgeBase.firmCount && `${knowledgeBase.firmCount}f`,
                                    knowledgeBase.companyCount && `${knowledgeBase.companyCount}c`,
                                  ]
                                    .filter(Boolean)
                                    .join(", ") || "0 items"}
                                  )
                                </span>
                              )}
                            </span>
                          </Tag>
                        )}
                        {fileUpload?.enabled &&
                          fileList.slice(0, 5).map(file => (
                            <Tag
                              data-cy="chat-file-upload-tag"
                              key={file.uid}
                              closable
                              onClose={() => {
                                setFileList(
                                  fileList.filter(f => f.uid !== file.uid)
                                );
                              }}
                              // status as the chip icon + color (like the knowledge-base
                              // chip) instead of a bare icon crammed inside a plain gray
                              // chip — keeps the uploaded-file chips clean + consistent.
                              color={
                                file.status === "done"
                                  ? "success"
                                  : file.status === "error"
                                    ? "error"
                                    : "default"
                              }
                              icon={
                                file.status === "uploading" ? (
                                  <CircularProgress data-cy="chat-file-uploading-indicator" size={14} />
                                ) : file.status === "done" ? (
                                  <CheckCircleOutlined data-cy="chat-file-done-indicator" />
                                ) : file.status === "error" ? (
                                  <CloseCircleOutlined data-cy="chat-file-error-indicator" />
                                ) : undefined
                              }
                              style={{ maxWidth: "100%" }}
                            >
                              <span
                                style={{
                                  maxWidth: "200px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                }}
                              >
                                {file.url && !file.url.includes("PutObject") ? (
                                  <a
                                    data-cy="chat-file-upload-link"
                                    href={file.url}
                                    download={file.name}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    // inherit the chip's contrast text (white on the
                                    // green "done" chip) instead of the default blue
                                    // link colour, which was unreadable on green.
                                    style={{ color: "inherit" }}
                                  >
                                    {file.name}
                                  </a>
                                ) : (
                                  file.name
                                )}
                              </span>
                            </Tag>
                          ))}
                        {fileUpload?.enabled && fileList.length > 5 && (
                          <Tag
                            data-cy="chat-file-upload-more"
                            style={{
                              transition: "all 0.3s ease-in-out",
                              backgroundColor: "#f0f0f0",
                              color: "#595959",
                              cursor: "default",
                            }}
                          >
                            +{fileList.length - 5} more
                          </Tag>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          </MarkdownCard>
        </DropZone>
      </Col>
    </Row>
  );
};
